// English FAQ, also shown for languages without their own FAQ
function FAQContent_en({ images }) {
  return (
    <>
      <h1>FAQ</h1>

      <h2>How do I get started?</h2>
      <a href="https://youtu.be/bJ-wX68WNHM" target="_blank" rel="noopener noreferrer">
        Video tutorial
      </a>

      <h2>General</h2>
      <b>Which Minecraft versions are supported?</b>
      <p>
        Java Edition 1.12.2 up to 26.3, selectable under <em>Settings</em>. If your version is not listed, pick the closest older one: Minecraft upgrades older
        schematics and map files when it loads them. Blocks that do not exist in the selected version are hidden from the block selection.
      </p>

      <b>Where do I report bugs or request features?</b>
      <p>
        On{" "}
        <a href="https://github.com/flyingfinger1/mapartcraft/issues" target="_blank" rel="noopener noreferrer">
          GitHub
        </a>
        . This version is a fork of{" "}
        <a href="https://github.com/rebane2001/mapartcraft" target="_blank" rel="noopener noreferrer">
          rebane2001's MapartCraft
        </a>
        , so please report problems with it here rather than to the original project.
      </p>

      <b>Why does the output change with the same image and settings?</b>
      <p>
        The JPEG decoding and scaling algorithms vary between browsers and get changed all the time. If you wish to be 100% sure your image stays the same,
        right click on the <em>Map preview</em> and choose <em>Save image as...</em>. Next time you can upload the image you saved.
      </p>

      <h2>Schematic (.nbt)</h2>
      <b>What do I do with the NBT file?</b>
      <p>Use it like a .schematic file.</p>

      <b>How can I use the NBT file?</b>
      <p>
        You can load it with mods like{" "}
        <a href="https://modrinth.com/mod/litematica" target="_blank" rel="noopener noreferrer">
          Litematica
        </a>{" "}
        (for Litematica the <a href="#litematic">.litematic download</a> is more convenient) and with programs like{" "}
        <a href="https://cubical.xyz/" target="_blank" rel="noopener noreferrer">
          cubical.xyz
        </a>
        . You can also import them into your game directly with a structure block - although it might require a redstone power source (
        <a href="https://github.com/rebane2001/mapartcraft/issues/100" target="_blank" rel="noopener noreferrer">
          #100
        </a>
        ). It might be a better idea to use a datafile instead - it's easier and gives more colors.
      </p>

      <b>...but I need a .schematic file!</b>
      <p>
        Import it into{" "}
        <a href="https://cubical.xyz/" target="_blank" rel="noopener noreferrer">
          cubical.xyz
        </a>{" "}
        or{" "}
        <a href="https://www.mcedit-unified.net/" target="_blank" rel="noopener noreferrer">
          MCEdit
        </a>{" "}
        and export as .schematic (only needed for old tools and versions). <em>Note that if you're using MCEdit or Cubical, you must export the map as 1.12.2.</em> You do not need a .schematic file for
        use with Baritone; use Schematica and the "#schematica" command.
      </p>

      <b>But MCEdit doesn't work!</b>
      <p>
        Make sure you're using{" "}
        <a href="https://www.mcedit-unified.net/" target="_blank" rel="noopener noreferrer">
          MCEdit Unified
        </a>{" "}
        and importing the NBT as a schematic, NOT loading it as a world. Alternatively, use Cubical.
      </p>

      <b>Why is there a row of extra blocks?</b>
      <p>
        The shade a block shows up as on the map is decided by the block North of it; if the Northern block is higher then a darker tone shows, else if the
        Northern block is lower then a lighter tone shows, else a normal tone shows. Thus an extra row of blocks (colloquially known as a noobline) exists at
        the top of a map to shade the top row properly.
      </p>
      <p>
        If you leave it out with <em>Exclude support/noobline from first row</em>, the shading of the top row depends on whatever is North of the map in your
        world, so only do this if you know what is there (for example when building next to an existing map).
      </p>

      <b>How do I align the map?</b>
      <p>
        When you have found a suitable place to build your map (eg above an ocean) make sure to open the map first and find the bottom left corner for aligning
        your schematic. Maps in Minecraft align to a fixed 128x128 grid. North is always the up-direction on maps and you shouldn't need to rotate the schematic.
      </p>

      <h2 id="litematic">Litematic (.litematic)</h2>
      <b>What is the .litematic file?</b>
      <p>
        The native schematic format of the{" "}
        <a href="https://modrinth.com/mod/litematica" target="_blank" rel="noopener noreferrer">
          Litematica
        </a>{" "}
        mod. It contains the same blocks as the NBT download, including support blocks and the noobline.
      </p>

      <b>How do I use it?</b>
      <p>
        Put the file into the <em>schematics</em> folder of your Minecraft directory, open the Litematica menu in-game (<em>M</em> by default), choose{" "}
        <em>Load Schematics</em> and load it. Place it aligned to the map grid as described above. Litematica can then show the schematic layer by layer and
        list the materials you still need.
      </p>

      <b>Can I download split .litematic files?</b>
      <p>
        Yes: "DOWNLOAD LITEMATIC" gives you one schematic for the whole map, "DOWNLOAD LITEMATIC AS 1x1 SPLIT" a .zip file with one schematic per map,
        just like the NBT download.
      </p>

      <h2 id="transparent">Transparent maps</h2>
      <b>How do I make parts of a map transparent?</b>
      <p>
        Enable <em>Transparency</em> under <em>Settings</em> and choose where the transparent parts come from: the transparency of the image (a PNG with
        transparent areas) or a <em>key colour</em>. With a key colour, fill everything that should be transparent with a colour that does not appear in the
        picture, for example bright magenta, and pick that colour; the tolerance also catches slightly different shades. The preview shows transparent pixels
        as a checkerboard. Turn off the <em>Background</em> option of the image preprocessing, it fills transparent areas.
      </p>

      <b>Where do I have to build a transparent map?</b>
      <p>
        A map pixel only stays transparent if nothing with a map colour is below it, down to the bottom of the world. Glass, glass panes, barriers and air
        have no map colour, so a map can be built in the void (for example in the End), or on a layer of glass or a floor of barriers, as long as nothing
        else is underneath. Stained and tinted glass do have a map colour. To check a place, create a new map there before you build: it should stay empty.
      </p>

      <b>Why is the row below a transparent part lighter?</b>
      <p>
        Minecraft shades every pixel by comparing its height with the pixel North of it. For a transparent pixel it uses the bottom of the world, so the pixel
        directly South of it is always higher and shows its light tone, also on flat maps. MapartCraft takes this into account: these pixels only use light
        tones, and the preview shows exactly what the map will look like. In return, the staircase may start again at any height after a transparent part,
        which keeps 3D maps lower.
      </p>

      <b>Which support blocks are used?</b>
      <p>
        The ones you chose under <em>Block to add</em>; nothing is ever placed where the map should be transparent. <em>glass</em> is a handy support block in
        general: it is invisible on maps. Because of that, the noobline is built from cobblestone when the support block is glass (or another block
        without map colour): the top row is shaded against it, so it has to be visible.
      </p>

      <h2>Datafile (.dat)</h2>
      <b>What is a map.dat file?</b>
      <p>
        '.dat' is the native format Minecraft stores map data in, meaning you can use it to import maps into your worlds without needing to build a physical
        structure. It also enables you to use a fourth extra shade of color not accessible in survival.
      </p>

      <b>What do I do with the map.dat file?</b>
      <p>
        You can use the map.dat file in singleplayer or a server you own. Create a new map in-game, go to your world's save file, then the data folder and from
        there you can replace map_xxx.dat files. MapartCraft downloads a .zip file containing all of the 1x1 map.dat files.
      </p>

      <h2>Settings</h2>
      <b>Map size?</b>
      <p>
        This will define how many maps you will create for your picture. When creating bigger maps, it's recommended to split it into multiple schematics (lest
        large staircased maps stretch above the world height limit). This can be done with the "DOWNLOAD NBT AS 1x1 SPLIT" and "DOWNLOAD LITEMATIC AS 1x1 SPLIT" buttons, which download all the 1x1 NBTs or litematics in a
        .zip file.
      </p>

      <b>My image is stretched!</b>
      <p>Change your map size, enable the crop option, or edit your image with an image editor.</p>

      <b>'Staircasing'?</b>
      <p>
        This will make your map 3D. Doing so will give you 3 times the colors, often producing a much richer mapart, but it will also make the map a lot harder
        to build, as it is not flat. 3D 'Classic' and 'Valley' modes produce the exact same resulting map image, however they are built differently; 'Valley' mode allows the map to be built without any downwards staircases, which may be easier in survival. Observe the difference:
      </p>
      <div style={{ textAlign: "center" }}>
        <img alt="classicVsValley.png" src={images.classicVsValley} style={{"maxWidth": "75%"}}></img>
      </div>
      <p>
        More staircasing modes can be enabled from the <em>Extras</em> settings tab.
      </p>

      <b>Can I rotate a 3D (staircased) mapart?</b>
      <p>
        No! Unlike flat maparts, 3D maparts rely on the North–South direction for their shading. Rotating the schematic will break the shading and the map will look wrong in-game. Always place the schematic in its original orientation. See also: <a href="https://github.com/rebane2001/mapartcraft/issues/297" target="_blank" rel="noopener noreferrer">#297</a>.
      </p>

      <b>Better color?</b>
      <p>
        This setting will give you more natural colors. Disabling this will make the website faster and give you slightly worse colors. It is recommended to
        keep this enabled.
      </p>

      <b>Dithering?</b>
      <p>
        This will add grain to your image to make it look a lot smoother. Floyd-Steinberg dithering is the most accurate, but the Ordered/Bayer dithering will
        usually have less artifacts and gives the image an unique style. It is usually recommended to disable dithering for flat-colored artwork.
      </p>
      <p>
        <em>Strength</em> sets how strongly to dither: around 50-70% keeps gradients smooth while flat areas get much less noise, which also makes the map
        easier to build. <em>Serpentine</em> (for Floyd-Steinberg and the other error diffusion methods) alternates the direction every row and avoids the
        diagonal streaks these methods otherwise produce.
      </p>

      <b>Add blocks under/Block to add?</b>
      <p>
        Here you can pick the block that will be put under either important blocks (eg carpets, sand, pressure plates) or all blocks. This block will also be
        used for the noobline. With 'Valley' staircasing, support blocks that no longer help placing anything are left out; support blocks under blocks that
        need them are always kept. The support block count in <em>Materials</em> is exactly what the schematic contains.
      </p>

      <b>Presets?</b>
      <p>
        You can use presets to save and load block configurations. Pick your blocks and click "Save" to save them as a preset, pick a preset to load it and
        click "Delete" to delete the loaded preset. It is also possible to share a link for your preset with others.
      </p>

      <h2>Custom Blocks</h2>
      <p>Custom blocks can be added from the bottom of the blocks selection pane. Different versions of a block can be added for the same block name, eg for 1.12.2 and 1.13.2+. Some examples are provided in the 'examples' section. NBT tags / block states can be found on the <a href="https://minecraft.wiki/w/Block_states" target="_blank" rel="noopener noreferrer">Minecraft Wiki</a>. To edit an existing custom block, select it, edit the tags / versions etc, and then click the 'add' button to overwrite. Note that presets URLs do not support custom blocks.</p>

      <div style={{ textAlign: "center" }}>
        <img alt="Palette" src={images.palette} />
      </div>
    </>
  );
}

FAQContent_en.closeLabel = "Close";

export default FAQContent_en;
